import type { PropsWithChildren } from 'react';
import React from 'react';

import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { SecondaryButtonV3 } from '@fragment/ui/src/components/Button/SecondaryButtonV3/SecondaryButtonV3';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import { LayoutRow } from '@fragment/ui/src/components/Payments/LayoutRow';

import { ToastProvider } from '../../wrappers/ToastProvider';

type PaymentViewLayoutProps = {
  title: string;
  primaryAction?: Action;
  secondaryAction?: Action;
  tertiaryAction?: Action;
  /** Icon button rendered at the far right of the header bar. */
  panelToggle?: React.ReactNode;
  'data-testid'?: string;
  onBack?: () => void;
  backTitle?: string;
};

export type Action = {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  disabledTooltipText?: string;
  'data-testid'?: string;
};

export const kActionButtonWidth = 'w-fit text-xmd';

const ActionButtons = ({
  primaryAction,
  secondaryAction,
  tertiaryAction,
}: {
  primaryAction?: Action;
  secondaryAction?: Action;
  tertiaryAction?: Action;
}) => (
  <div
    className="flex flex-row gap-x-[0.25lh] w-fit justify-between"
    data-testid="invoice-action-buttons"
  >
    {tertiaryAction && (
      <SecondaryButtonV3
        onClick={tertiaryAction.onClick}
        className={kActionButtonWidth}
        disabled={tertiaryAction.disabled}
        disabledTooltipText={tertiaryAction.disabledTooltipText}
        data-testid={tertiaryAction['data-testid']}
      >
        {tertiaryAction.title}
      </SecondaryButtonV3>
    )}
    {secondaryAction && (
      <SecondaryButtonV3
        onClick={secondaryAction.onClick}
        className={kActionButtonWidth}
        disabled={secondaryAction.disabled}
        disabledTooltipText={secondaryAction.disabledTooltipText}
        data-testid={secondaryAction['data-testid']}
      >
        {secondaryAction.title}
      </SecondaryButtonV3>
    )}
    {primaryAction && (
      <PrimaryButtonV3
        onClick={primaryAction.onClick}
        className={kActionButtonWidth}
        disabled={primaryAction.disabled}
        disabledTooltipText={primaryAction.disabledTooltipText}
        data-testid={primaryAction['data-testid']}
      >
        {primaryAction.title}
      </PrimaryButtonV3>
    )}
  </div>
);

export const PaymentViewLayout = ({
  children,
  title,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  panelToggle,
  onBack,
  backTitle,
  'data-testid': dataTestId,
}: PropsWithChildren<PaymentViewLayoutProps>) => (
  <ToastProvider>
    <div
      className="flex flex-col w-full h-fit min-h-[100dvh] font-sans pb-f2"
      data-testid={dataTestId ?? 'invoice-layout'}
    >
      <div className="flex flex-row w-full h-f8 items-start justify-between border-b-[0.5px] border-main-300 pt-f1 px-f1">
        {!onBack && (
          <span
            className="text-xmd text-main"
            data-testid="payment-view-header"
          >
            {title}
          </span>
        )}
        {onBack && (
          <div className="text-xmd text-main">
            <TextButton
              onClick={onBack}
              icon={<Icon type="left" size="sm" primaryColor="gray" />}
              hoverIcon={<Icon type="left" size="sm" primaryColor="black" />}
            >
              {backTitle && (
                <span className="font-sans text-xmd">{backTitle}</span>
              )}
            </TextButton>
          </div>
        )}
        <div className="flex items-center gap-x-[0.25lh]">
          {(primaryAction || secondaryAction || tertiaryAction) && (
            <ActionButtons
              primaryAction={primaryAction}
              secondaryAction={secondaryAction}
              tertiaryAction={tertiaryAction}
            />
          )}
          {panelToggle}
        </div>
      </div>
      {children}
    </div>
  </ToastProvider>
);

type HeaderProps = {
  disabled?: boolean;
};

export const PaymentViewHeader = ({
  children,
  disabled,
}: PropsWithChildren<HeaderProps>) => (
  <LayoutRow height="medium" justify="between" hide={disabled}>
    {children}
  </LayoutRow>
);

export const PaymentViewLoading = ({
  message = 'Loading...',
}: {
  message?: string;
}) => (
  <PaymentViewLayout title="Loading...">
    <div className="text-main-500">{message}</div>
  </PaymentViewLayout>
);
